import type { JSONContent } from '@tiptap/react';
import type { Schema } from '@tiptap/pm/model';
import type { DocNode } from '../components/DocContent';
import { ALLOWED_IMAGE_MIME, type UploadedImage } from '../../rich-editor/parts/editorHelpers.ts';

const MAX_FILE_SIZE_BYTES = 800 * 1024 * 1024;

const DRAFT_IMAGE_PLACEHOLDER_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="96" height="72" viewBox="0 0 96 72">' +
    '<rect width="96" height="72" rx="8" fill="#f3f4f6"/>' +
    '<g transform="translate(34.5 21.75) scale(1.5)" fill="none" stroke="#9ca3af" ' +
    'stroke-linecap="round" stroke-linejoin="round">' +
    '<rect x="2.25" y="2.75" width="13.5" height="13.5" rx="1.5"/>' +
    '<circle cx="6.75" cy="7.25" r="1.5"/><path d="M15.75 11.75 12.4 9 4.5 16.25"/></g></svg>';

export const DRAFT_IMAGE_PLACEHOLDER_SRC = `data:image/svg+xml,${encodeURIComponent(DRAFT_IMAGE_PLACEHOLDER_SVG)}`;

const ALLOWED_GIF_HOSTS = [
    'media.giphy.com',
    'media0.giphy.com',
    'media1.giphy.com',
    'media2.giphy.com',
    'media3.giphy.com',
    'media4.giphy.com',
    'media.tenor.com',
    'c.tenor.com'
];

export function isAllowedGifUrl(url: unknown): url is string {
    if (typeof url !== 'string') return false;
    try {
        const { protocol, hostname } = new URL(url);
        return (
            protocol === 'https:' &&
            ALLOWED_GIF_HOSTS.some((host) => hostname === host || hostname.endsWith(`.${host}`))
        );
    } catch {
        return false;
    }
}

// A doc-supplied src is trusted only for picker GIFs, which carry no fileId.
export function imageNodeUrl(
    attrs: Record<string, unknown> | undefined,
    resolveFileUrl?: (fileId: string) => string | undefined
): string | undefined {
    const fileId = attrs?.fileId;
    if (typeof fileId === 'string') return resolveFileUrl?.(fileId);
    const src = attrs?.src;
    return isAllowedGifUrl(src) ? src : undefined;
}

export function inlineImageNode({ url, fileId }: UploadedImage): JSONContent {
    return { type: 'image', attrs: { src: url, fileId } };
}

export function routeComposerFiles(
    files: File[],
    inlineImages: boolean
): { inline: File[]; tray: File[] } {
    const sized = files.filter((file) => file.size <= MAX_FILE_SIZE_BYTES);
    const isInline = (file: File): boolean =>
        inlineImages && (ALLOWED_IMAGE_MIME as readonly string[]).includes(file.type);
    return { inline: sized.filter(isInline), tray: sized.filter((file) => !isInline(file)) };
}

function inlineFileId(node: DocNode): string | undefined {
    const fileId = node.attrs?.fileId;
    return node.type === 'image' && typeof fileId === 'string' ? fileId : undefined;
}

export function collectInlineImageSrcs(doc: DocNode): Record<string, string> {
    const fileId = inlineFileId(doc);
    const src = doc.attrs?.src;
    const own = fileId && typeof src === 'string' ? { [fileId]: src } : {};
    return (doc.content ?? []).reduce<Record<string, string>>(
        (srcs, child) => ({ ...srcs, ...collectInlineImageSrcs(child) }),
        own
    );
}

export function withResolvedImageSrcs(
    doc: DocNode,
    resolveFileUrl?: (fileId: string) => string | undefined
): DocNode {
    const fileId = inlineFileId(doc);
    const url = fileId ? resolveFileUrl?.(fileId) : undefined;
    return {
        ...doc,
        ...(url && { attrs: { ...doc.attrs, src: url } }),
        ...(doc.content && {
            content: doc.content.map((child) => withResolvedImageSrcs(child, resolveFileUrl))
        })
    };
}

// Object URLs die with the page, so a stored draft shows file images as a placeholder until sent.
export function withDraftImagePlaceholders(doc: DocNode): DocNode {
    return withResolvedImageSrcs(doc, () => DRAFT_IMAGE_PLACEHOLDER_SRC);
}

// Null for a doc the composer schema can't hold (a mention, a table): its edit must not send a
// bodyDoc, or the server reads the missing image nodes as removed and deletes their files.
export function editableDoc(
    schema: Schema,
    doc: DocNode | undefined,
    resolveFileUrl?: (fileId: string) => string | undefined
): JSONContent | null {
    if (!doc) return null;
    const resolved = withResolvedImageSrcs(doc, resolveFileUrl) as JSONContent;
    try {
        schema.nodeFromJSON(resolved);
        return resolved;
    } catch {
        return null;
    }
}
